import React, { useState, useEffect, useRef } from 'react';
import { Field, Formik, FormikProps } from 'formik';
import { connect } from 'react-redux';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import BixReadonlySelect from '$/components/global/bixReadonlySelect';
import InputBlock from '$/components/global/inputBlock';
import { updateDraft } from '$/store/draft/actions';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
dayjs.extend(localeData);
dayjs().localeData();

export interface ICompanySupplementalProps {
  currentCompany?: Bixby.Models.AdminCompany;
  draftCompany?: Bixby.Models.DraftCompany;
  isEditing?: boolean;
  discardDraft?: boolean;
  accountingTypes?: Bixby.Models.IIdentifiable[];
  currencies?: Bixby.Models.IIdentifiable[];
  debtIssuanceTypes?: Bixby.Models.IIdentifiable[];
  dispatchUpdateDraftCompany: (companyId: string, updatedCompany: Bixby.Models.JsonPatchOperation[]) => void;
}

export interface CompanySupplementalValues {
  fiscalYearEnd: string;
  daysFiscalYearEndReporting: string;
  daysQuarterlyReporting: string;
  currency_Id: string;
  accountingType_Id: string;
  reportingFrequency: string;
  debtIssuanceType_Id: string;
}

const CompanySupplemental: React.FC<ICompanySupplementalProps> = props => {
  const {
    currentCompany,
    draftCompany,
    isEditing,
    discardDraft,
    accountingTypes,
    currencies,
    debtIssuanceTypes,
    dispatchUpdateDraftCompany,
  } = props;
  const [initialFormValues, setInitialFormValues] = useState({} as CompanySupplementalValues);

  const monthOptions: Bixby.Models.IIdentifiable[] = dayjs.monthsShort().map(x => {
    return {
      id: x,
      name: x,
    };
  });

  const reportingFrequencyOptions: Bixby.Models.IIdentifiable[] = [
    { id: 'Monthly', name: 'Monthly' },
    { id: 'Quarterly', name: 'Quarterly' },
    { id: 'Semi-annual', name: 'Semi-annual' },
    { id: 'Other', name: 'Other' },
  ];

  const formikRef = useRef<FormikProps<CompanySupplementalValues>>(null);

  useEffect(() => {
    if (!isEditing && currentCompany && !Bixby.Utils.isNullOrEmpty(currentCompany)) {
      setInitialFormValues({
        fiscalYearEnd: dayjs.utc(currentCompany.fiscalYearEnd).format('MMM'),
        daysFiscalYearEndReporting: currentCompany.daysFiscalYearEndReporting,
        daysQuarterlyReporting: currentCompany.daysQuarterlyReporting,
        currency_Id: currentCompany.currency_Id,
        accountingType_Id: currentCompany.accountingType_Id,
        reportingFrequency: currentCompany.reportingFrequency,
        debtIssuanceType_Id: currentCompany.debtIssuanceType_Id,
      });
    } else if (isEditing && !Bixby.Utils.isNullOrEmpty(draftCompany) && draftCompany) {
      setInitialFormValues({
        fiscalYearEnd: dayjs.utc(draftCompany.fiscalYearEnd).format('MMM'),
        daysFiscalYearEndReporting: draftCompany.daysFiscalYearEndReporting,
        daysQuarterlyReporting: draftCompany.daysQuarterlyReporting,
        currency_Id: draftCompany.currency_Id,
        accountingType_Id: draftCompany.accountingType_Id,
        reportingFrequency: draftCompany.reportingFrequency,
        debtIssuanceType_Id: draftCompany.debtIssuanceType_Id,
      });
    }
  }, [isEditing, currentCompany, draftCompany]);

  useEffect(() => {
    if (discardDraft) {
      handleResetForm();
    }
  }, [discardDraft]);

  const handleResetForm = () => {
    if (formikRef && formikRef.current) {
      formikRef.current.resetForm();
    }
  };

  return !Bixby.Utils.isNullOrEmpty(initialFormValues) ? (
    <div className="admin-profile-section bottom">
      <Formik
        innerRef={formikRef}
        initialValues={initialFormValues}
        enableReinitialize
        onSubmit={(values: CompanySupplementalValues) => {
          const commandArray = Object.entries(values).map(field => {
            return {
              op: 'replace',
              path: `/${field[0]}`,
              value: field[1],
              from: '',
            } as Bixby.Models.JsonPatchOperation;
          });
          if (draftCompany) {
            dispatchUpdateDraftCompany(draftCompany.id, commandArray);
          }
        }}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <div className="profile-input-container">
              <div className="profile-input-column">
                <h3>Supplementary Company Information</h3>
                <BixReadonlySelect
                  title="Fiscal year end"
                  fieldName="fiscalYearEnd"
                  fieldId={initialFormValues.fiscalYearEnd}
                  showDefaultOption={true}
                  isEditing={isEditing}
                  options={monthOptions}
                  sort={false}
                />
                <InputBlock title="Days FYE reporting">
                  <Field type="number" name="daysFiscalYearEndReporting" disabled={!isEditing} />
                </InputBlock>
                <InputBlock title="Days Q reporting">
                  <Field type="number" name="daysQuarterlyReporting" disabled={!isEditing} />
                </InputBlock>
                <BixReadonlySelect
                  title="Currency"
                  fieldName="currency_Id"
                  fieldId={initialFormValues.currency_Id}
                  showDefaultOption={true}
                  isEditing={isEditing}
                  options={currencies}
                />
                <BixReadonlySelect
                  title="Accounting"
                  fieldName="accountingType_Id"
                  fieldId={initialFormValues.accountingType_Id}
                  showDefaultOption={true}
                  isEditing={isEditing}
                  options={accountingTypes}
                />
                <BixReadonlySelect
                  title="Reporting Frequency"
                  fieldName="reportingFrequency"
                  fieldId={initialFormValues.reportingFrequency}
                  showDefaultOption={true}
                  isEditing={isEditing}
                  options={reportingFrequencyOptions}
                />
                <BixReadonlySelect
                  title="Debt Issuance"
                  fieldName="debtIssuanceType_Id"
                  fieldId={initialFormValues.debtIssuanceType_Id}
                  showDefaultOption={true}
                  isEditing={isEditing}
                  options={debtIssuanceTypes}
                />
              </div>
            </div>
            {isEditing && (
              <button className="bix_button" type="submit">
                Save as Draft
              </button>
            )}
          </form>
        )}></Formik>
    </div>
  ) : null;
};

const mapStateToProps = (state: AppState) => {
  return {
    isLoading: state.draftState.isLoading,
    error: state.draftState.error,
    draftCompany: state.draftState.draftCompany,
    isEditing: state.draftState.isEditing,
    discardDraft: state.draftState.discardDraft,
    currentCompany: state.companyState.currentCompany,
    accountingTypes: state.accountingTypeState.data,
    currencies: state.currenciesState.data,
    debtIssuanceTypes: state.debtIssuanceTypeState.data,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchUpdateDraftCompany: (companyId: string, draftCompanyCommand: Bixby.Models.JsonPatchOperation[]) =>
    dispatch(updateDraft(companyId, draftCompanyCommand)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanySupplemental);
